import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import html2pdf from "html2pdf.js";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  Printer,
  Loader2,
  Download,
  Compass,
  Shield,
  CheckCircle2,
  MapPin,
  Phone,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CARE_TYPE_LABEL = {
  home_care: "Home Care",
  home_health: "Home Health",
  palliative_care: "Palliative Care",
  hospice_care: "Hospice Care",
  senior_advisor: "Senior Advisor",
  adult_day_care: "Adult Day Care",
  home_physicians: "Home Physicians",
  community_partner: "Community Partner",
};

function humanCareType(code) {
  if (!code) return "";
  return CARE_TYPE_LABEL[code] || code.split("_").map((s) => s[0].toUpperCase() + s.slice(1)).join(" ");
}

function sanitizeForFilename(str) {
  return (str || "form").replace(/[^a-zA-Z0-9-]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40);
}

export default function FormPrintPage() {
  const { formId } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const paperRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/portal/forms/${formId}`)
      .then((res) => setForm(res.data))
      .catch(() => setError("Unable to load this form. Please sign in and try again."));
  }, [formId]);

  const handleDownloadPdf = async () => {
    if (!paperRef.current || downloading) return;
    setDownloading(true);
    try {
      const recipientSlug = sanitizeForFilename(form.care_recipient_name);
      const dateSlug = new Date(form.signed_at).toISOString().slice(0, 10);
      const filename = `Tyvena-Care-Selection-${recipientSlug}-${dateSlug}.pdf`;
      await html2pdf()
        .from(paperRef.current)
        .set({
          margin: [10, 8, 10, 8],
          filename,
          image: { type: "jpeg", quality: 0.96 },
          html2canvas: { scale: 2, useCORS: true, backgroundColor: "#ffffff", letterRendering: true },
          jsPDF: { unit: "mm", format: "letter", orientation: "portrait", compress: true },
          pagebreak: { mode: ["css", "legacy"] },
        })
        .save();
      toast.success("PDF downloaded", { description: filename });
    } catch (e) {
      toast.error("Couldn't generate PDF", { description: "Please try Print instead and choose 'Save as PDF'." });
    } finally {
      setDownloading(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center gap-4 p-8">
        <p className="text-text-secondary font-body">{error}</p>
        <Button data-testid="print-error-back-btn" onClick={() => navigate("/portal")} className="bg-primary text-white rounded-full font-body">
          Back to Portal
        </Button>
      </div>
    );
  }

  if (!form) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const signedDate = new Date(form.signed_at);
  const signedDateLong = signedDate.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  const signedDateShort = signedDate.toLocaleDateString("en-US");
  const signedTime = signedDate.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  const shortId = (form.id || "").slice(0, 8).toUpperCase();

  return (
    <div className="min-h-screen bg-[#F1F5F9] print:bg-white">
      {/* Toolbar - hidden when printing / in PDF */}
      <div className="print:hidden bg-white border-b border-[#E2E8F0] px-4 py-3 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto flex flex-wrap items-center justify-between gap-3">
          <Button data-testid="print-back-btn" onClick={() => navigate("/portal")} variant="ghost" className="text-primary hover:bg-primary/5 font-body">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Portal
          </Button>
          <div className="flex gap-2">
            <Button
              data-testid="print-now-btn"
              onClick={() => window.print()}
              variant="outline"
              className="border-2 border-primary text-primary hover:bg-primary/5 rounded-full font-body font-bold"
            >
              <Printer className="w-4 h-4 mr-2" /> Print
            </Button>
            <Button
              data-testid="download-pdf-btn"
              onClick={handleDownloadPdf}
              disabled={downloading}
              className="bg-primary text-white hover:bg-primary-dark rounded-full font-body font-bold shadow-md disabled:opacity-60"
            >
              {downloading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Preparing PDF…</>
              ) : (
                <><Download className="w-4 h-4 mr-2" /> Download PDF</>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Print CSS — hide toolbar & tighten margins */}
      <style>{`
        @media print {
          @page { size: letter; margin: 0.5in; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .no-print { display: none !important; }
        }
      `}</style>

      {/* Printable / PDF paper */}
      <div
        ref={paperRef}
        data-testid="printable-form"
        className="max-w-[8.5in] mx-auto bg-white shadow-lg print:shadow-none my-6 print:my-0"
        style={{ minHeight: "10.5in", color: "#1E293B", fontFamily: '"Didact Gothic", ui-sans-serif, system-ui, sans-serif' }}
      >
        {/* LETTERHEAD */}
        <div style={{ background: "linear-gradient(135deg, #0D3D5C 0%, #14547A 55%, #2AADA0 100%)", padding: "28px 44px", color: "white" }}>
          <div className="flex items-start justify-between gap-6">
            <div className="flex items-center gap-4">
              <div style={{ width: 60, height: 60, borderRadius: 12, overflow: "hidden", boxShadow: "0 4px 14px rgba(0,0,0,0.25)", background: "#2AADA0", flexShrink: 0 }}>
                <img src="/tyvena-mark-512.png" alt="Tyvena" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              </div>
              <div>
                <div style={{ fontSize: 12, letterSpacing: "0.2em", fontWeight: 700, color: "#E07B54", textTransform: "uppercase", marginBottom: 2 }}>
                  Your threshold to what comes next
                </div>
                <div style={{ fontFamily: 'Manrope, ui-sans-serif, system-ui, sans-serif', fontSize: 26, fontWeight: 800, lineHeight: 1.1, letterSpacing: "-0.02em" }}>
                  Tyvena — Care Provider Selection
                </div>
                <div style={{ fontSize: 12, opacity: 0.75, marginTop: 4 }}>
                  A signed record of the care providers this family has chosen.
                </div>
              </div>
            </div>
            <div style={{ textAlign: "right", fontSize: 11, opacity: 0.9 }}>
              <div style={{ fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#E07B54", marginBottom: 4 }}>Form ID</div>
              <div style={{ fontFamily: 'ui-monospace, "SFMono-Regular", monospace', fontSize: 13, letterSpacing: "0.05em" }}>{shortId}</div>
              <div style={{ marginTop: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#E07B54" }}>Signed</div>
              <div style={{ fontSize: 12 }}>{signedDateLong}</div>
              <div style={{ fontSize: 11, opacity: 0.8 }}>at {signedTime}</div>
            </div>
          </div>
        </div>

        {/* BODY */}
        <div style={{ padding: "36px 44px 24px 44px" }}>
          {/* For-office chip */}
          <div className="flex items-center gap-2 mb-6">
            <div style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 12px", border: "1px solid #2AADA0", borderRadius: 999, fontSize: 10, color: "#0D3D5C", fontWeight: 700, letterSpacing: "0.15em", textTransform: "uppercase", background: "#EDF7F6" }}>
              <Shield style={{ width: 12, height: 12 }} />
              For Your Care Team's Records
            </div>
          </div>

          {/* Statement */}
          <div style={{ background: "#FAF9F6", borderLeft: "4px solid #E07B54", padding: "18px 22px", borderRadius: 6, marginBottom: 28 }}>
            <div style={{ fontSize: 10, color: "#475569", fontWeight: 700, letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: 6 }}>
              Provider Selection Statement
            </div>
            <p style={{ fontSize: 16, lineHeight: 1.55, color: "#1E293B", margin: 0 }}>
              At this time, for{" "}
              <strong style={{ color: "#0D3D5C", borderBottom: "2px solid #E07B54", paddingBottom: 1 }}>
                {form.care_recipient_name}
              </strong>
              , we are choosing the following care provider{form.providers.length !== 1 ? "s" : ""}:
            </p>
          </div>

          {/* Provider cards */}
          <div style={{ marginBottom: 30 }}>
            <div style={{ fontSize: 11, color: "#475569", fontWeight: 700, letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
              <Compass style={{ width: 14, height: 14, color: "#2AADA0" }} />
              Chosen Providers ({form.providers.length})
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {form.providers.map((p, idx) => (
                <div
                  key={p.id}
                  data-testid={`pdf-provider-${idx}`}
                  style={{
                    border: "1px solid #E2E8F0",
                    borderLeft: "5px solid #2AADA0",
                    borderRadius: 8,
                    padding: "14px 18px",
                    background: "white",
                    breakInside: "avoid",
                    pageBreakInside: "avoid",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, marginBottom: 6 }}>
                    <div style={{ fontFamily: 'Manrope, ui-sans-serif, system-ui, sans-serif', fontSize: 16, fontWeight: 700, color: "#0D3D5C", lineHeight: 1.2 }}>
                      {p.name}
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4, justifyContent: "flex-end" }}>
                      {(p.care_types || []).map((ct) => (
                        <span key={ct} style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", background: "#EDF7F6", color: "#0D3D5C", padding: "3px 8px", borderRadius: 4, whiteSpace: "nowrap" }}>
                          {humanCareType(ct)}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div style={{ fontSize: 12, color: "#475569", display: "flex", flexWrap: "wrap", gap: "4px 20px", lineHeight: 1.5 }}>
                    {p.phone && (
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                        <Phone style={{ width: 12, height: 12, color: "#E07B54" }} />
                        {p.phone}
                      </span>
                    )}
                    {p.address && (
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                        <MapPin style={{ width: 12, height: 12, color: "#E07B54" }} />
                        {p.address}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Attestation */}
          <div style={{ fontSize: 12, color: "#475569", fontStyle: "italic", lineHeight: 1.6, borderTop: "1px dashed #CBD5E1", borderBottom: "1px dashed #CBD5E1", padding: "14px 0", marginBottom: 28 }}>
            {form.attestation_text}
          </div>

          {/* Signature block */}
          <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 30, breakInside: "avoid", pageBreakInside: "avoid" }}>
            <div>
              <div style={{ fontSize: 10, color: "#475569", fontWeight: 700, letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: 8 }}>
                Signature
              </div>
              {form.signature_drawn && (
                <div style={{ maxWidth: 300, marginBottom: 6, borderBottom: "2px solid #0D3D5C", paddingBottom: 2 }}>
                  <img src={form.signature_drawn} alt="Drawn signature" style={{ maxWidth: "100%", maxHeight: 90, display: "block" }} />
                </div>
              )}
              {!form.signature_drawn && (
                <div style={{ fontFamily: '"Great Vibes", "Brush Script MT", cursive', fontSize: 34, fontStyle: "italic", color: "#0D3D5C", borderBottom: "2px solid #0D3D5C", display: "inline-block", paddingBottom: 2, paddingRight: 40, lineHeight: 1.1 }}>
                  {form.signature_typed}
                </div>
              )}
              <div style={{ fontSize: 12, color: "#1E293B", marginTop: 6, fontWeight: 600 }}>
                {form.signed_by_name}
              </div>
              <div style={{ fontSize: 11, color: "#475569" }}>
                {form.signed_by_email}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 10, color: "#475569", fontWeight: 700, letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: 8 }}>
                Date Signed
              </div>
              <div style={{ fontSize: 18, color: "#1E293B", borderBottom: "2px solid #0D3D5C", display: "inline-block", paddingBottom: 4, paddingRight: 20, fontWeight: 600 }}>
                {signedDateShort}
              </div>
              <div style={{ fontSize: 11, color: "#475569", marginTop: 6 }}>at {signedTime}</div>

              <div style={{ marginTop: 20, display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", background: "#EDF7F6", border: "1px solid #2AADA0", borderRadius: 8 }}>
                <CheckCircle2 style={{ width: 18, height: 18, color: "#2AADA0", flexShrink: 0 }} />
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "#0D3D5C", textTransform: "uppercase", letterSpacing: "0.1em" }}>Electronically Signed</div>
                  <div style={{ fontSize: 10, color: "#475569" }}>Audit trail on file</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* FOOTER */}
        <div style={{ padding: "18px 44px 28px 44px", borderTop: "1px solid #E2E8F0", marginTop: 20, background: "#FAF9F6" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 20, flexWrap: "wrap" }}>
            <div style={{ fontSize: 10, color: "#64748B", lineHeight: 1.55, maxWidth: 480 }}>
              This document was electronically signed via the Tyvena Care Navigation secure family portal on{" "}
              <strong>{signedDateLong}</strong> at <strong>{signedTime}</strong>. A full audit trail — including IP address, session
              identifier, and time-stamped signature — is retained on file. This selection may be updated at any time by signing a new form
              in the portal.
            </div>
            <div style={{ textAlign: "right", fontSize: 10, color: "#64748B" }}>
              <div style={{ fontFamily: 'Manrope, ui-sans-serif, system-ui, sans-serif', fontWeight: 800, fontSize: 12, color: "#0D3D5C" }}>Tyvena</div>
              <div>myasher.app</div>
              <div style={{ marginTop: 2, fontFamily: 'ui-monospace, monospace' }}>ID · {form.id}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Helper text below paper */}
      <div className="no-print max-w-4xl mx-auto px-4 pb-10 text-center">
        <p className="text-sm text-text-secondary font-body">
          Download as PDF to attach to an email, or print to hand a copy to your care team.
        </p>
      </div>
    </div>
  );
}
